import { transport } from "@/src/config/apiConfig.js";

export default {
  // ------------------------------ Form APIs ------------------------------
  forms: {
    async fetchViews(entityType) {
      try {
        const res = await transport.get(`/api/form/views`, {
          params: {
            entity_type: entityType || "form",
          },
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching views: ", error);
        throw error;
      }
    },

    async applyFilter(viewId, payload) {
      try {
        const res = await transport.put(`/api/views/${viewId}`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while applying filter: ", error);
        throw error;
      }
    },

    async fetchFilteredForms(
      viewId,
      page,
      pageSize,
      searchQuery,
      filters = {},
      sorts = {},
    ) {
      try {
        const params = {
          page: page,
          page_size: pageSize,
        };
        if (searchQuery) {
          params.search_query = searchQuery;
        }
        if (filters && Object.keys(filters).length > 0) {
          params.filters = JSON.stringify(filters);
        }
        if (sorts && Object.keys(sorts).length > 0) {
          params.sorts = JSON.stringify(sorts);
        }
        const res = await transport.get(`/api/views/forms/${viewId}/results`, {
          params,
        });
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching filtered forms: ",
          error,
        );
        throw error;
      }
    },

    async listForms(page, pageSize, status) {
      try {
        const params = {
          page: page,
          page_size: pageSize,
        };
        if (status) {
          params.status = status;
        }
        const res = await transport.get(`/api/forms/v1`, { params });
        return res.data;
      } catch (error) {
        console.error("An error occurred while listing forms: ", error);
        throw error;
      }
    },

    async searchForms(query, filters, sorts, page, pageSize) {
      try {
        const res = await transport.get(`/api/forms/v1/search`, {
          params: {
            q: query,
            filters: filters ? JSON.stringify(filters) : undefined,
            sorts: sorts ? JSON.stringify(sorts) : undefined,
            page: page,
            page_size: pageSize,
          },
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while searching forms: ", error);
        throw error;
      }
    },

    async fetchFormDetails(formId) {
      try {
        const res = await transport.get(`/api/forms/v1/${formId}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching form details: ", error);
        throw error;
      }
    },

    async createForm(payload) {
      try {
        const res = await transport.post(`/api/forms/v1`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating form: ", error);
        throw error;
      }
    },

    async updateForm(formId, payload) {
      try {
        const res = await transport.put(`/api/forms/v1/${formId}`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating form: ", error);
        throw error;
      }
    },

    async deleteForm(formId) {
      try {
        const res = await transport.delete(`/api/forms/v1/${formId}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting form: ", error);
        throw error;
      }
    },

    async publishForm(formId) {
      try {
        const res = await transport.put(`/api/forms/v1/${formId}/publish`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while publishing form: ", error);
        throw error;
      }
    },

    async unpublishForm(formId) {
      try {
        const res = await transport.put(`/api/forms/v1/${formId}/unpublish`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while unpublishing form: ", error);
        throw error;
      }
    },

    async reindexForms() {
      try {
        const res = await transport.post(`/api/forms/v1/reindex`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while reindexing forms: ", error);
        throw error;
      }
    },

    async fetchPrefillData(formId, entityType, entityId) {
      try {
        const res = await transport.post(`/api/forms/v1/${formId}/prefill`, {
          entity_type: entityType,
          entity_id: entityId,
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching prefill data: ", error);
        throw error;
      }
    },
  },

  // ------------------------------ Form Versions APIs ------------------------------
  versions: {
    async listVersions(formId) {
      try {
        const res = await transport.get(`/api/forms/v1/${formId}/versions`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while listing versions: ", error);
        throw error;
      }
    },

    async createVersion(formId, schemaJson, rulesJson = null) {
      try {
        const payload = { schema_json: schemaJson };
        if (rulesJson) {
          payload.rules_json = rulesJson;
        }
        const res = await transport.post(
          `/api/forms/v1/${formId}/versions`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating version: ", error);
        throw error;
      }
    },

    async getLatestVersion(formId) {
      try {
        const res = await transport.get(
          `/api/forms/v1/${formId}/versions/latest`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching latest version: ",
          error,
        );
        throw error;
      }
    },

    async getPublishedVersion(formId) {
      try {
        const res = await transport.get(
          `/api/forms/v1/${formId}/versions/published`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching published version: ",
          error,
        );
        throw error;
      }
    },

    async getVersion(formId, versionId) {
      try {
        const res = await transport.get(
          `/api/forms/v1/${formId}/versions/${versionId}`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching version: ", error);
        throw error;
      }
    },

    // Update version with optional schema_json and/or rules_json
    async updateVersion(
      formId,
      versionId,
      { schemaJson = null, rulesJson = null } = {},
    ) {
      try {
        const payload = {};
        if (schemaJson !== null) {
          payload.schema_json = schemaJson;
        }
        if (rulesJson !== null) {
          payload.rules_json = rulesJson;
        }
        const res = await transport.put(
          `/api/forms/v1/${formId}/versions/${versionId}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating version: ", error);
        throw error;
      }
    },

    async publishVersion(formId, versionId) {
      try {
        const res = await transport.post(
          `/api/forms/v1/${formId}/versions/${versionId}/publish`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while publishing version: ", error);
        throw error;
      }
    },
  },

  // ------------------------------ Form Settings APIs ------------------------------
  settings: {
    async fetchFormSettings(formId) {
      try {
        const res = await transport.get(`/api/forms/v1/${formId}/settings`);
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching form settings: ",
          error,
        );
        throw error;
      }
    },

    async updateFormSettings(formId, payload) {
      try {
        const res = await transport.put(
          `/api/forms/v1/${formId}/settings`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while updating form settings: ",
          error,
        );
        throw error;
      }
    },

    async fetchFormSubmissions(formId, page, pageSize, filters = null) {
      try {
        const payload = {
          form_id: parseInt(formId),
          page: page,
          page_size: pageSize,
        };

        if (filters && Object.keys(filters).length > 0) {
          payload.filters = filters;
        }

        const res = await transport.post(
          `/api/forms/v1/${formId}/submissions/list`,
          payload,
        );

        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching form submissions: ",
          error,
        );
        throw error;
      }
    },

    async exportFormSubmissions(formId, payload) {
      try {
        const res = await transport.post(
          `/api/forms/v1/${formId}/submissions/export`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while exporting form submissions: ",
          error,
        );
        throw error;
      }
    },

    async fetchFormAnalytics(formId) {
      try {
        const res = await transport.get(`/api/forms/v1/${formId}/analytics`);
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching form analytics: ",
          error,
        );
        throw error;
      }
    },

    async fetchSubmissionDetails(submissionId, formId) {
      try {
        const res = await transport.get(
          `/api/forms/v1/${formId}/submissions/${submissionId}`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching submission details: ",
          error,
        );
        throw error;
      }
    },

    async updateSubmission(submissionId, values) {
      try {
        const res = await transport.put(
          `/api/forms/v1/submissions/${submissionId}`,
          { values },
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating submission: ", error);
        throw error;
      }
    },

    async getSubmissionById(submissionId) {
      try {
        const res = await transport.get(
          `/api/forms/v1/submissions/${submissionId}`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching submission by ID: ",
          error,
        );
        throw error;
      }
    },

    async getAttendeeSuggestions(entityType, entityId) {
      try {
        const params = {};
        if (entityType) params.entity_type = entityType;
        if (entityId) params.entity_id = entityId;
        const res = await transport.get(
          `/api/forms/v1/submissions/attendee-suggestions`,
          { params },
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching attendee suggestions: ",
          error,
        );
        throw error;
      }
    },
  },

  // ------------------------------ Form Schema APIs ------------------------------
  schema: {
    async fetchFormSchema(id) {
      try {
        const res = await transport.get(`/api/forms/v1/${id}/schema`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching form schema: ", error);
        throw error;
      }
    },
  },
};
